import React, { useState, useEffect, memo } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Clear from '@material-ui/icons/Clear';
import Tune from '@material-ui/icons/Tune';
import useTheme from '@material-ui/core/styles/useTheme';

import DateTime from '../../Shared/DateTimeSummary';
import AudienceSummary from './FilterSummary';
import DataGrid from './DataGrid';
import Sentence from './Sentence';
import Map from './Map';

import Logo from '../../Assets/Images/Logo';
import ChangeDashboard from '../../Assets/Images/ChangeDashboard';
import Logout from '../../Auth/Logout';

import { serializeAudienceQueryString } from '../../../lib/queryString';
import { queryAudience } from '../../../lib/smartStepsApi';
import { AggregationLevel } from '../../../lib/gridResults';
import { RefinementState } from '../../../types/refinement';
import { ApiAudienceDataState } from '../../../types/apiData';
import { AudienceQuery, Interval } from '../../../types/smartStepsApi';
import { ViewStatus } from '../../../types/global';

import { StyledPaper } from '../../../styles/MaterialComponents';
import DashboardStyles, { AsideStyles } from './Dashboard.styles';
import { appInsights } from '../../../lib/insights';
import { PostalPermission } from '../../../lib/postal';

// Memoize child components
const GridMemo = memo(DataGrid);
const MapMemo = memo(Map);
const DateTimeMemo = memo(DateTime);
const AudienceSummaryMemo = memo(AudienceSummary);
const SentenceMemo = memo(Sentence);

type AggregationSelections = {
  [AggregationLevel.NUTS1]: string[];
  [AggregationLevel.NUTS2]: string[];
  [AggregationLevel.NUTS3]: string[];
  [AggregationLevel.Area]: string[];
  [AggregationLevel.District]: string[];
  [AggregationLevel.Sector]: string[];
};

const initialAggregationSelections: AggregationSelections = {
  [AggregationLevel.NUTS1]: [],
  [AggregationLevel.NUTS2]: [],
  [AggregationLevel.NUTS3]: [],
  [AggregationLevel.Area]: [],
  [AggregationLevel.District]: [],
  [AggregationLevel.Sector]: [],
};

// Component
const Dashboard = (props: {
  initialQuery: AudienceQuery;
  initialRefinement: RefinementState;
  postalPermission: PostalPermission[];
}) => {
  // Local State
  const [refinement, setRefinement] = useState<RefinementState>(props.initialRefinement);
  const [apiData, setApiData] = useState<ApiAudienceDataState>({
    loading: false,
    count: 0,
    results: undefined,
  });
  const [dataFilter, setDataFilter] = useState<AudienceQuery>(props.initialQuery);
  const [aggregationSelections, setAggregationSelections] = useState(initialAggregationSelections);
  const [hovered, setHovered] = useState<string | undefined>(undefined);
  const [currentSelected, setCurrentSelected] = useState<string[]>(
    initialAggregationSelections[refinement.aggregationLevel]
  );
  const [lastSelected, setLastSelected] = useState<string | undefined>(undefined);
  const [viewStatus, setViewStatus] = useState<ViewStatus>(undefined);

  const [sizeMap, setSizeMap] = useState<boolean>(true);

  // Handlers

  function handleMapSize() {
    if (viewStatus !== 'mini') {
      setSizeMap(!sizeMap);
    }
  }

  function handleDateTime(interval: Interval) {
    setDataFilter((old) => ({
      ...old,
      dateRange: interval.dateRange,
      timeOfDay: interval.timeOfDay,
    }));
  }

  const toggleSelected = (value: string) => {
    const currentSelected = aggregationSelections[refinement.aggregationLevel];
    const newSelected = aggregationSelections;

    if (currentSelected.includes(value)) {
      setLastSelected(undefined);

      newSelected[refinement.aggregationLevel] = currentSelected.filter((s) => s !== value);
    } else {
      setLastSelected(value);

      newSelected[refinement.aggregationLevel] = [...currentSelected, value];
    }

    setCurrentSelected(newSelected[refinement.aggregationLevel]);
    setAggregationSelections(newSelected);
  };

  const clearSelected = () => {
    const newSelected = aggregationSelections;

    newSelected[refinement.aggregationLevel] = [];

    setLastSelected(undefined);
    setCurrentSelected(newSelected[refinement.aggregationLevel]);
    setAggregationSelections(newSelected);
  };

  // Effects
  const theme = useTheme();
  const miniView = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    miniView ? setViewStatus('mini') : setViewStatus('full');
  }, [miniView]);

  useEffect(() => {
    setAggregationSelections(initialAggregationSelections);
  }, [dataFilter]);

  useEffect(() => {
    // Update query string
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}?${serializeAudienceQueryString({
        query: dataFilter,
        refinement,
      })}`
    );
  }, [dataFilter, refinement]);

  useEffect(() => {
    // Query API
    setApiData((data) => ({ ...data, loading: true }));

    queryAudience(dataFilter)
      .then((data) => {
        setApiData({ ...data, loading: false });
      })
      .catch((err) => {
        appInsights?.trackException({ exception: err });
        setApiData((data) => ({ count: 0, results: undefined, loading: false }));
      });
  }, [dataFilter]);

  // Render
  return (
    <DashboardStyles viewStatus={viewStatus} mapSize={sizeMap}>
      <AsideStyles viewStatus={viewStatus}>
        <div className="audience-summary">
          <div className="logo-container">
            <div className="brand-logo">
              <Logo size="md" align="left" viewStatus={viewStatus} linkToHome />
            </div>

            {viewStatus === 'mini' ? (
              <div id="open-btn">
                <Tune
                  onClick={() => {
                    setViewStatus('full-with-screen');
                  }}
                />
              </div>
            ) : null}
          </div>

          <div className="scroller">
            <div className="scroller-content">
              <div className="date-and-time">
                <DateTimeMemo interval={dataFilter} onSubmit={handleDateTime} />
              </div>
              <div className="filter-summary">
                <AudienceSummaryMemo
                  audienceFilter={dataFilter}
                  onChange={(audienceFilter) => {
                    setDataFilter((filter) => ({ ...filter, ...audienceFilter }));
                  }}
                />
              </div>
              <ChangeDashboard linkTo="profiles" />
              <ChangeDashboard linkTo="catchment" />
            </div>
          </div>
        </div>
        {viewStatus === 'full-with-screen' ? (
          <>
            <div id="close-btn">
              <Clear
                onClick={() => {
                  setViewStatus('mini');
                }}
              />
            </div>
            <div
              className="screen"
              onClick={() => {
                setViewStatus('mini');
              }}
            ></div>
          </>
        ) : null}
      </AsideStyles>
      <main className="motion-bg">
        <div className="content">
          <div className="sentence-and-grid">
            <div className="sentence">
              <StyledPaper>
                <SentenceMemo
                  refinement={refinement}
                  onChange={setRefinement}
                  isLoading={apiData.loading}
                />
              </StyledPaper>
            </div>
            <div className="grid">
              <StyledPaper className="gridPaper">
                <GridMemo
                  apiData={apiData}
                  refinement={refinement}
                  setHovered={setHovered}
                  selected={currentSelected}
                  toggleSelected={toggleSelected}
                  clearSelected={clearSelected}
                  lastSelected={lastSelected}
                  resize={sizeMap}
                  postalPermission={props.postalPermission}
                />
              </StyledPaper>
            </div>
          </div>
          <div className="map">
            <MapMemo
              aggregationLevel={refinement.aggregationLevel}
              filter={refinement.filter}
              apiData={apiData}
              selected={currentSelected}
              setSelected={toggleSelected}
              hovered={hovered}
              onMapSizeChange={handleMapSize}
              resize={sizeMap}
              viewStat={viewStatus}
              postalPermission={props.postalPermission}
            />
          </div>
        </div>
        <div className="login">
          <Logout />
        </div>
      </main>
    </DashboardStyles>
  );
};

export default Dashboard;
